import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Typography } from "../../../components/__common/Typography/Typography";
import "./Consignment.scss";

export interface ConsignmentColumnGroup {
  header: React.ReactNode;
  columns: React.ReactNode[][];
}

export interface ConsignmentData {
  groups: ConsignmentColumnGroup[];
}

export const useConsignmentPageEffect = () => {
  useEffect(() => {
    const main = document.getElementById("main-body");
    if (!main) return;

    main.style.height = "100vh";
    main.style.marginTop = "0";
    main.style.overflow = "hidden";
    main.classList.add("consignmentPageBg");

    return () => {
      main.style.height = "";
      main.style.marginTop = "";
      main.style.overflow = "auto";
      main.classList.remove("consignmentPageBg");
    };
  }, []);
};

interface ConsignmentProps {
  data: ConsignmentData;
  testId: string;
}

export const Consignment: React.FC<ConsignmentProps> = ({ data, testId }) => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === "ko";

  useConsignmentPageEffect();

  const rowCount = data.groups[0]?.columns[0]?.length ?? 0;

  return (
    <div className="consignment" data-testid={testId}>
      <div className={classNames("table-container", { "kr-character": isKo })}>
        <table cellSpacing="0">
          <thead>
            <tr>
              {data.groups.map((group, groupIdx) => (
                <th key={`group-${groupIdx}`} colSpan={group.columns.length}>
                  <Typography variant="body2" weight="w400">
                    {group.header}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }).map((_, rowIdx) => {
              const getContentKey = (content: React.ReactNode): string => {
                if (typeof content === 'string' || typeof content === 'number') {
                  return String(content);
                }
                return `row${rowIdx}`;
              };
              const rowKey = data.groups[0]?.columns[0]?.[rowIdx]
                ? getContentKey(data.groups[0].columns[0][rowIdx])
                : `row${rowIdx}`;
              return (
                <tr key={`row-${rowKey}`}>
                  {data.groups.map((group, groupIdx) =>
                    group.columns.map((col, colIdx) => {
                      const cellContent = col[rowIdx];
                      const cellKey = `${groupIdx}-${colIdx}-${
                        typeof cellContent === 'string' || typeof cellContent === 'number'
                          ? String(cellContent)
                          : `row${rowIdx}`
                      }`;
                      return (
                        <td key={cellKey}>
                          <Typography variant="body2" weight="w400">
                            {cellContent}
                          </Typography>
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
